import { useContext } from 'react';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import UserProfileContext from '../../context/userprofilecontext.js';
import { formatDate, formatCurrency, formatOwnershipAllocation, collectAbbreviationLegend } from '../../utils/formatters.js';
import { PERSONAL_ASSIGNMENT_KEY, ENTITY_ASSIGNMENTS_LABEL } from '../../utils/accountAssignment';
import {
    addFootersToAllPages,
    addPageIfNeeded,
    normalizeMargin,
    sanitizeForHelvetica,
    standardPDFStyles,
} from '../shared/PDFCommon.js';

/*
 * PDF export for the Accounts & Net Worth page.
 *
 * Renders from the already-computed page state (account groups, totals, net
 * worth trend points), not from the DOM -- the on-screen page groups accounts
 * into cards with expandable holdings, which a browser print would either
 * clip or dump unexpanded; rendering from data avoids both.
 */

const MARGIN = 40;

// Exported for direct unit testing -- pure formatting helpers, no jsPDF doc
// involved, so they don't need the jsPDF-mocking ceremony the sibling
// PDFPrint*.test.js files use for the doc-drawing functions.
export const money = (value) => (value === null || value === undefined ? '-' : `$${formatCurrency(value)}`);
const text = (value) => sanitizeForHelvetica(value ?? '');

const addTitle = (doc, pageWidth, yPosition, { currentCase, totals }) => {
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text(text(currentCase.caseName || 'Petitioner v. Respondent'), pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 18;

    doc.setFontSize(12);
    doc.text('Accounts & Net Worth', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 18;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(
        `Net Worth: ${money(totals.netWorth)}    Assets: ${money(totals.assets)}    Liabilities: ${money(totals.liabilities)}`,
        pageWidth / 2, yPosition, { align: 'center' },
    );
    yPosition += 20;

    return yPosition;
};

// Simple vector line+area plot of the net worth trend, mirroring NetWorthChart's
// shape (area fill down to the zero baseline) without the SVG chart's axis-tick
// niceties -- proportional to a PDF export, not a screen-parity requirement.
const addNetWorthTrend = (doc, margin, yPosition, netWorthData) => {
    if (!netWorthData || netWorthData.length < 2) return yPosition;

    const pageWidth = doc.internal.pageSize.getWidth();
    const plotW = pageWidth - margin.left - margin.right;
    const plotH = 120;
    const plotTop = yPosition;
    const plotBottom = yPosition + plotH;

    const balances = netWorthData.map((p) => p.balance);
    const min = Math.min(0, ...balances);
    const max = Math.max(0, ...balances);
    const range = max - min || 1;

    const toX = (i) => margin.left + (i / (netWorthData.length - 1)) * plotW;
    const toY = (val) => plotBottom - ((val - min) / range) * plotH;

    doc.setDrawColor(...standardPDFStyles.table.body.default.lineColor);
    doc.setLineWidth(0.5);
    doc.rect(margin.left, plotTop, plotW, plotH);

    // Zero baseline, when it falls inside the plotted range.
    if (min < 0 && max > 0) {
        doc.setLineWidth(0.3);
        doc.setDrawColor(180, 180, 180);
        const zeroY = toY(0);
        doc.line(margin.left, zeroY, margin.left + plotW, zeroY);
    }

    doc.setDrawColor(20, 184, 166); // --color-accent-500
    doc.setLineWidth(1.2);
    for (let i = 1; i < netWorthData.length; i++) {
        doc.line(toX(i - 1), toY(balances[i - 1]), toX(i), toY(balances[i]));
    }

    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(text(formatDate(netWorthData[0].date)), margin.left, plotBottom + 10);
    doc.text(text(formatDate(netWorthData[netWorthData.length - 1].date)), margin.left + plotW, plotBottom + 10, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    return plotBottom + 24;
};

const ACCOUNT_COLUMNS = [
    { header: 'Account', dataKey: 'name' },
    { header: 'Type', dataKey: 'type' },
    { header: 'Title', dataKey: 'title' },
    { header: 'Balance', dataKey: 'balance', columnStyle: { halign: 'right' } },
    { header: 'Change', dataKey: 'change', columnStyle: { halign: 'right' } },
];

export const accountRow = (account) => ({
    name: text(account._displayName || account.bank_name || 'Account'),
    type: text(account.account_type_label || account.account_type || ''),
    // Abbreviated like the on-screen row; the legend at the end maps each
    // short form back to the full name.
    title: text(formatOwnershipAllocation(account.title, { abbreviate: true }) || 'Untitled'),
    balance: money(account.latest_balance),
    // Matches the on-screen row, which only renders a change badge when
    // balance_change is non-null AND non-zero -- a literal zero change
    // showing "$0.00" here while the screen shows nothing would be a
    // PDF-vs-screen inconsistency on a document meant to mirror the screen.
    change: (account.balance_change != null && account.balance_change !== 0)
        ? money(account.balance_change)
        : '-',
});

const addSectionHeading = (doc, margin, yPosition, label, fontSize) => {
    yPosition = addPageIfNeeded(doc, margin, yPosition);
    doc.setFontSize(fontSize);
    doc.setFont(undefined, 'bold');
    doc.text(text(label), margin.left, yPosition);
    return yPosition + fontSize + 4;
};

// Mirrors the on-screen list: one section per assignment (Personal, then each
// business / other entity) when any account is entity-titled, each holding the
// usual type groups; a single unlabeled section otherwise.
const addAccountSections = (doc, margin, yPosition, listSections) => {
    let entitiesHeadingShown = false;
    for (const section of listSections) {
        const { assignment } = section;
        if (assignment) {
            const isPersonal = assignment.key === PERSONAL_ASSIGNMENT_KEY;
            if (!isPersonal && !entitiesHeadingShown) {
                yPosition = addSectionHeading(doc, margin, yPosition, ENTITY_ASSIGNMENTS_LABEL, 13);
                entitiesHeadingShown = true;
            }
            const label = assignment.kind ? `${assignment.label} (${assignment.kind})` : assignment.label;
            yPosition = addSectionHeading(doc, margin, yPosition, `${label} — Net ${money(section.net)}`, 11);
        }
        yPosition = addAccountGroups(doc, margin, yPosition, section.visibleGroups, section.grouped);
    }
    return yPosition;
};

const addTitleLegend = (doc, margin, yPosition, accounts) => {
    const legend = collectAbbreviationLegend((accounts || []).map((a) => a.title));
    if (!legend.length) return yPosition;
    yPosition = addSectionHeading(doc, margin, yPosition, 'Abbreviations', 10);
    autoTable(doc, {
        columns: [
            { header: 'Abbreviation', dataKey: 'abbreviation' },
            { header: 'Full name', dataKey: 'display_name' },
        ],
        body: legend.map((l) => ({ abbreviation: text(l.abbreviation), display_name: text(l.display_name) })),
        startY: yPosition,
        styles: { font: 'helvetica', fontSize: 8 },
        headStyles: {
            fillColor: standardPDFStyles.table.header.default.fillColor,
            textColor: standardPDFStyles.table.header.default.textColor,
        },
        margin: { top: margin.top, left: margin.left, right: margin.right, bottom: margin.bottom },
    });
    return (doc.lastAutoTable?.finalY ?? yPosition + 20) + 16;
};

const addAccountGroups = (doc, margin, yPosition, visibleGroups, grouped) => {
    for (const groupKey of visibleGroups) {
        const group = grouped[groupKey];
        yPosition = addPageIfNeeded(doc, margin, yPosition);

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text(`${text(group.label)} — ${money(group.total)}`, margin.left, yPosition);
        yPosition += 6;

        autoTable(doc, {
            columns: ACCOUNT_COLUMNS,
            body: group.accounts.map(accountRow),
            startY: yPosition,
            styles: {
                font: 'helvetica',
                fontSize: 8,
                lineColor: standardPDFStyles.table.body.default.lineColor,
                lineWidth: standardPDFStyles.table.body.default.lineWidth,
            },
            headStyles: {
                fillColor: standardPDFStyles.table.header.default.fillColor,
                textColor: standardPDFStyles.table.header.default.textColor,
            },
            columnStyles: ACCOUNT_COLUMNS.reduce((acc, col, index) => {
                acc[index] = col.columnStyle || {};
                return acc;
            }, {}),
            margin: { top: margin.top, left: margin.left, right: margin.right, bottom: margin.bottom },
        });

        yPosition = (doc.lastAutoTable?.finalY ?? yPosition + 20) + 16;
    }
    return yPosition;
};

/*
 * Builds the document and returns it, without saving. Kept free of React so
 * it can be generated headlessly, mirroring buildStatementQualityPDF.
 * Returns null when there is nothing to print.
 */
export const buildAccountsNetWorthPDF = ({
    accounts,
    visibleGroups,
    grouped,
    totals,
    netWorthData,
    listSections,
    currentCase = {},
    currentOrg = {},
    displayOptions = { isDraft: true },
}) => {
    if (!accounts || accounts.length === 0) return null;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    doc.setFont('Helvetica', 'normal');

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = normalizeMargin(MARGIN);

    let yPosition = margin.top;
    yPosition = addTitle(doc, pageWidth, yPosition, { currentCase, totals });
    yPosition = addNetWorthTrend(doc, margin, yPosition, netWorthData);
    const sections = listSections || [{ assignment: null, visibleGroups, grouped }];
    yPosition = addAccountSections(doc, margin, yPosition, sections);
    addTitleLegend(doc, margin, yPosition, accounts);

    // Last, so every page exists and the page count is right.
    addFootersToAllPages(doc, currentOrg, displayOptions || {}, MARGIN);

    return doc;
};

export const accountsNetWorthFilename = (currentCase = {}) => {
    const caseSlug = currentCase.caseName
        ? currentCase.caseName.replace(/[^a-zA-Z0-9]/g, '_')
        : 'Document';
    return `Accounts_And_Net_Worth_${caseSlug}_${new Date().toISOString().split('T')[0]}.pdf`;
};

/*
 * Plain function returning a closure, matching usePrintStatementQualityPDF /
 * usePrintMBSPDF. Calls React hooks, so it must be invoked at the top level of
 * a component and ABOVE any early return.
 */
const usePrintAccountsNetWorthPDF = ({ accounts, visibleGroups, grouped, totals, netWorthData, listSections }) => {
    const userProfileContext = useContext(UserProfileContext);
    const currentCase = userProfileContext?.userProfile?.currentCase || {};
    const currentOrg = userProfileContext?.userProfile?.currentOrg || {};

    return () => {
        const doc = buildAccountsNetWorthPDF({
            accounts, visibleGroups, grouped, totals, netWorthData, listSections, currentCase, currentOrg,
        });
        if (!doc) return;
        doc.save(accountsNetWorthFilename(currentCase));
    };
};

export default usePrintAccountsNetWorthPDF;
